import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgxCurrencyDirective } from 'ngx-currency';
import { merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { MaskService } from '../../../../../shared/services/mask.service';
import { ProdutoService } from '../../../../../shared/services/apis/produto/produto.service';
import { ProdutoListaRefinamentoEnum } from '../../../../../shared/enums/produto/produto-lista-refinamento.enum';
import { ProdutoListStruct } from '../../../../../shared/services/structs/produto/produto-list.struct';
import { codigoFciValidator } from '../../../../../shared/validators/codigo-fci.validator';
import { UtilService } from '../../../../../shared/services/util.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { FciItemRequest } from '@shared/services/requests/motor-fiscal/fci-item.request';

export interface ItemFciDialogData {
  item: FciItemRequest | null;
}

// Valor parcela não pode superar Valor saída — validador de grupo (campos cruzados).
function parcelaMenorQueSaidaValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const valorSaida = group.get('valorSaida')?.value ?? 0;
    const valorParcela = group.get('valorParcela')?.value ?? 0;
    const parcelaControl = group.get('valorParcela');
    const outrosErros = { ...(parcelaControl?.errors ?? {}) };
    delete outrosErros['parcelaMaiorQueSaida'];

    if (valorSaida > 0 && valorParcela > valorSaida) {
      parcelaControl?.setErrors({ ...outrosErros, parcelaMaiorQueSaida: true });
    } else if (parcelaControl?.hasError('parcelaMaiorQueSaida')) {
      parcelaControl.setErrors(Object.keys(outrosErros).length ? outrosErros : null);
    }
    return null;
  };
}

@Component({
  selector: 'app-item-fci-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    NgxCurrencyDirective,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './item-fci-dialog.component.html',
  styleUrl: './item-fci-dialog.component.css',
})
export class ItemFciDialogComponent implements OnInit {
  public form: FormGroup;

  // Dados do produto selecionado — nunca editáveis (só leitura na tela): emitir a FCI
  // com dados diferentes do cadastro real do produto geraria divergência no documento fiscal.
  public produtoSelecionado: {
    idProduto: number; descricao: string; codigoSku: string | null;
    ncm: string; unidade: string; gtin: string | null;
  } | null = null;

  public buscaProduto = '';
  public resultadosBusca: ProdutoListStruct[] = [];
  public buscando = false;
  private searchInput$ = new Subject<string>();

  public moneyMask = this.maskService.getCurrencyMask(2);
  public percentMask = this.maskService.getPercentMask(2);
  public masks = this.maskService.getMasks();
  // Restringe o "A" da máscara nfci a hexadecimal (A-F/0-9) — sem isso o ngx-mask aceita qualquer letra.
  public readonly codigoFciPatterns = { A: { pattern: /[A-Fa-f0-9]/ } };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ItemFciDialogData,
    public dialogRef: MatDialogRef<ItemFciDialogComponent>,
    private produtoService: ProdutoService,
    private maskService: MaskService,
    private formBuilder: FormBuilder,
  ) {
    this.form = this.formBuilder.group({
      valorSaida: [0, [Validators.required, Validators.min(0.01)]],
      valorParcela: [0, [Validators.required, Validators.min(0.01)]],
      percentualConteudoImportacao: [0, [Validators.required, Validators.min(0.01), Validators.max(100)]],
      codigoFci: [null, [codigoFciValidator()]],
    }, { validators: parcelaMenorQueSaidaValidator() });
  }

  ngOnInit(): void {
    if (this.data.item) {
      const item = this.data.item;
      this.produtoSelecionado = {
        idProduto: item.idProduto,
        descricao: item.descricao,
        codigoSku: item.codigoSku,
        ncm: item.ncm,
        unidade: item.unidade,
        gtin: item.gtin,
      };
      this.buscaProduto = item.descricao;
      this.form.patchValue({
        valorSaida: item.valorSaida,
        valorParcela: item.valorParcela,
        percentualConteudoImportacao: item.percentualConteudoImportacao,
        codigoFci: item.codigoFci,
      });
    }

    // Sugestão automática do % de conteúdo de importação a partir de saída/parcela — o
    // usuário pode sobrescrever livremente, é só um ponto de partida (patchValue acima
    // usa o padrão de emitir evento, mas roda antes desta subscription, então não é afetado).
    merge(
      this.form.get('valorSaida')!.valueChanges,
      this.form.get('valorParcela')!.valueChanges,
    ).subscribe(() => {
      const valorSaida = this.form.get('valorSaida')?.value ?? 0;
      const valorParcela = this.form.get('valorParcela')?.value ?? 0;
      const percentual = valorSaida > 0 ? UtilService.arredondarAbnt((valorParcela / valorSaida) * 100, 2) : 0;
      this.form.get('percentualConteudoImportacao')?.setValue(percentual, { emitEvent: false });
    });

    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(texto => {
          if (!texto || texto.trim().length < 2) {
            this.buscando = false;
            return [];
          }
          this.buscando = true;
          return this.produtoService.getAll({
            searchText: texto,
            idProdutoListaRefinamento: ProdutoListaRefinamentoEnum.Descricao,
            isAtivo: true,
          }, 0);
        }),
      )
      .subscribe({
        next: (res: any) => {
          this.buscando = false;
          this.resultadosBusca = res?.isError ? [] : (res?.list ?? []);
        },
        error: () => {
          this.buscando = false;
          this.resultadosBusca = [];
        },
      });
  }

  public onBuscaChange(): void {
    this.searchInput$.next(this.buscaProduto);
  }

  public selecionarProduto(produto: ProdutoListStruct): void {
    this.produtoSelecionado = {
      idProduto: produto.idProduto,
      descricao: produto.descricao.slice(0, 120),
      codigoSku: produto.codigoSku ?? null,
      ncm: produto.ncm ?? '',
      unidade: produto.unidade ?? '',
      gtin: produto.gtinEan ?? null,
    };
    this.form.patchValue({ valorSaida: produto.preco ?? 0 });
    this.buscaProduto = this.produtoSelecionado.descricao;
    this.resultadosBusca = [];
  }

  // Só força maiúsculo — a formatação com traços já é feita pelo [mask]="masks.nfci".
  public onCodigoFciInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const upper = input.value.toUpperCase();
    if (upper !== input.value) {
      input.value = upper;
    }
    this.form.get('codigoFci')?.setValue(upper, { emitEvent: false });
  }

  public get codigoFciValido(): boolean {
    const control = this.form.get('codigoFci');
    return !!control?.value && !control.hasError('codigoFciInvalido');
  }

  public salvar(): void {
    this.form.markAllAsTouched();

    if (!this.produtoSelecionado || this.form.invalid) {
      return;
    }

    const item: FciItemRequest = {
      idProduto: this.produtoSelecionado.idProduto,
      descricao: this.produtoSelecionado.descricao,
      codigoSku: this.produtoSelecionado.codigoSku,
      ncm: this.produtoSelecionado.ncm,
      unidade: this.produtoSelecionado.unidade,
      gtin: this.produtoSelecionado.gtin,
      valorSaida: this.form.value.valorSaida,
      valorParcela: this.form.value.valorParcela,
      percentualConteudoImportacao: this.form.value.percentualConteudoImportacao,
      codigoFci: this.form.value.codigoFci || null,
    };

    this.dialogRef.close({ confirmed: true, item });
  }

  public cancelar(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
